import React, { FC, useEffect, useRef } from 'react';
import { FilterComponent } from '../FilterComponent';
import { SelectPicker } from 'rsuite';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { ProviderSelector } from './ProviderSelector';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router';
import { makeSearchParams, parseSearchParams } from '../../utils/utilities';
import { usePaymentContext } from '../../store/payments/payments-context';

export interface IProviderFiltersProps {
  isActive?: string;
  partnerId?: string;
  providerId?: string;
}

interface Props {
  isActive?: string;
  partnerId?: string;
  setIsActive: (value: string) => void;
  setPartnerId: (value: string) => void;
}

export const ProviderFilters: FC<Props> = (props) => {

  const { isActive, partnerId, setIsActive, setPartnerId } = props;

  const { t } = useTranslation();
  const isMounted = useRef(false);
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const { merchantState: { merchantsLists, whitelabelPartnersListSelector }} = useMerchants();
  const { paymentContextState: { isProviderDataLoading }} = usePaymentContext();

  const filtersFromUrl: IProviderFiltersProps = parseSearchParams(search);

  const getStatusFilter = (): string => {

    switch (isActive) {
      case '1': return 'true';
      case '2': return 'false';
      default: return 'null';
    }
  };

  useEffect(() => {
    if (!isMounted.current) return;
    const params: IProviderFiltersProps = { ...filtersFromUrl };

    params.isActive = getStatusFilter();
    params.partnerId = partnerId;
    params.providerId = filtersFromUrl?.providerId;

    navigate(`${pathname}?${makeSearchParams(params)}`);
  }, [isActive, partnerId]);

  useEffect(() => {
    if (!isMounted.current) {
      // first mount
      isMounted.current = true;
    }
  }, []);

  return (
    <div className="d-flex justify-content-between align-items-center">

      <div className="d-flex align-items-center fc-filter-line">
        <FilterComponent value={isActive} label={t('MERCHANTS_LIST.SELECT_STATUS')} labelPlacement="integrated">
          <SelectPicker
            data={merchantsLists.merchantStatuses}
            appearance="subtle"
            placement="bottomEnd"
            placeholder={t('MERCHANTS_LIST.SELECT_STATUS')}
            onSelect={(value): void => setIsActive(value)}
            onClean={(): void => setIsActive('')}
            className={clsx('fc-selector timezone-selector', isActive ? 'fc-selector-cleanable' : 'fc-added-icon')}
            searchable={false}
            value={isActive}
            disabled={isProviderDataLoading}
            data-cy="status-filter"
          />
        </FilterComponent>

        <FilterComponent value={partnerId} label={t('MENU.PARTNERS')} labelPlacement="integrated">
          <SelectPicker
            data={whitelabelPartnersListSelector}
            appearance="subtle"
            placement="bottomEnd"
            placeholder={t('MENU.PARTNERS')}
            onSelect={(pid): void => setPartnerId(pid)}
            onClean={(): void => setPartnerId('')}
            className={clsx('fc-selector timezone-selector', partnerId ? 'fc-selector-cleanable' : 'fc-added-icon')}
            value={partnerId}
            disabled={isProviderDataLoading}
            data-cy="pid-filter"
          />
        </FilterComponent>
      </div>

      <ProviderSelector/>
    </div>
  );
};